import useQueryBuildings from "ApiLayer/Building/queryBuildings";
import Card from "components/molecules/Card";
import { getCardValueColor } from "utils/helpers";

interface ReportsIncrementalNetPresentValueCardProps {
    filtersetUid: string;
    reportedVsPublished: string;
}

export function ReportsIncrementalNetPresentValueCard({
    filtersetUid,
    reportedVsPublished,
}: ReportsIncrementalNetPresentValueCardProps) {
    const { isLoading, data: buildingsResponse } = useQueryBuildings(filtersetUid, reportedVsPublished);

    if (isLoading) {
        return <Card data={undefined} loading={true} cardTitle="Cost and Savings" />;
    }

    let minYear = 0;
    let maxYear = 0;
    const runningTotals = {
        years: 0,
        totalMeasureCost: 0,
        totalLikeForLikeCost: 0,
        totalIncrementalCost: 0,
        totalIncentives: 0,
        totalEnergyCostSavings: 0,
        totalCarbonPolicyCostSavings: 0,
        returnOnInvestment: 0,
        netIncrementalCosts: 0,
        splitNetIncrementalCost: 0,
        netSavings: 0,
        splitNetSavings: 0,
        revenue: 0,
    };

    buildingsResponse!.buildings.forEach((building) => {
        if (!building.reportedCrp || !building.baselineCrp) {
            return;
        }
        const crp: CarbonReductionPlan = building.reportedCrp!;

        if (crp.startYear > minYear) {
            minYear = crp.startYear;
        }
        if (crp.endYear > maxYear) {
            maxYear = crp.endYear;
        }

        runningTotals.totalMeasureCost += -crp.splitPlanLifetimeTotalMeasureCosts;
        runningTotals.totalLikeForLikeCost += -(
            crp.splitPlanLifetimeTotalMeasureCosts - crp.splitPlanLifetimeIncrementalMeasureCosts
        );
        runningTotals.totalIncrementalCost += -crp.splitPlanLifetimeIncrementalMeasureCosts;
        runningTotals.totalIncentives += crp.planLifetimeIncentives;
        runningTotals.totalEnergyCostSavings += crp.splitPlanLifetimeEnergyCostSavings;
        runningTotals.totalCarbonPolicyCostSavings += crp.planLifetimeCarbonPolicySavings;

        const splitNetIncrementalCost = crp.annualSummaries.reduce(
            (acc: number, item: AnnualSummary) => acc + item.splitNetIncrementalCost,
            0
        );
        runningTotals.splitNetIncrementalCost += splitNetIncrementalCost;

        runningTotals.netIncrementalCosts += crp.splitPlanLifetimeIncrementalMeasureCosts - crp.planLifetimeIncentives;

        const splitNetSavings = crp.annualSummaries.reduce(
            (acc: number, item: AnnualSummary) => acc + item.splitNetSavings,
            0
        );
        runningTotals.splitNetSavings += splitNetSavings;

        runningTotals.netSavings +=
            crp.splitPlanLifetimeEnergyCostSavings + crp.planLifetimeCarbonPolicySavings + crp.planLifetimeRevenue;
        runningTotals.revenue += crp.planLifetimeRevenue;
    });

    runningTotals.years = maxYear - minYear;
    runningTotals.returnOnInvestment =
        ((runningTotals.netSavings - runningTotals.netIncrementalCosts) / runningTotals.netIncrementalCosts) * 100;

    const netTotalCost = runningTotals.totalMeasureCost - runningTotals.totalIncentives;
    const firstSection = [
        {
            displayInfoKey: "years",
            value: runningTotals.years,
        },
        {
            displayInfoKey: "totalMeasureCost",
            title: "Net Cost",
            value: netTotalCost,
            valueColor: getCardValueColor(netTotalCost),
        },
        {
            displayInfoKey: "totalLikeForLikeCost",
            value: runningTotals.totalLikeForLikeCost,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "incrementalCost",
            value: runningTotals.totalIncrementalCost,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalIncentives",
            value: runningTotals.totalIncentives,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalCostSavings",
            title: "Net Savings",
            value: runningTotals.netSavings,
            valueColor: getCardValueColor(runningTotals.netSavings),
        },
        {
            displayInfoKey: "totalCarbonPolicyCostSavings",
            value: runningTotals.totalCarbonPolicyCostSavings,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalEnergyCostSavings",
            value: runningTotals.totalEnergyCostSavings,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "revenue",
            value: runningTotals.revenue,
            titleIcon: <>• </>,
        },
    ];
    const data = {
        header: {
            displayInfoKey: "splitNetSavings",
            value: runningTotals.splitNetSavings,
            valueColor: getCardValueColor(runningTotals.splitNetSavings),
        },
        sections: [firstSection],
    };
    return <Card loading={isLoading} data={data} cardTitle="Cumulative Net Present Value" />;
}

interface BuildingIncrementalNetPresentValueCardProps {
    buildingModel: Building;
    selectedBMCRP: BuildingModelCarbonReductionPlan;
}

export function BuildingIncrementalNetPresentValueCard({
    buildingModel,
    selectedBMCRP,
}: BuildingIncrementalNetPresentValueCardProps) {
    const crp: CarbonReductionPlan = selectedBMCRP.plan;
    const planLifetimeNPVIncremental = crp.carbonReductionMeasures.reduce(
        (acc: number, item: CarbonReductionMeasure) => acc + item.netPresentValueIncremental,
        0
    );
    const netTotalCost = crp.splitPlanLifetimeTotalMeasureCosts - crp.planLifetimeIncentives;
    const netTotalSavings =
        crp.splitPlanLifetimeEnergyCostSavings + crp.planLifetimeCarbonPolicySavings + crp.planLifetimeRevenue;
    const firstSection = [
        {
            displayInfoKey: "discountRate",
            value: crp.carbonReductionPlanOptions.netPresentValueDiscountRate * 100,
        },
        {
            displayInfoKey: "years",
            value: crp.endYear - crp.startYear,
        },
        {
            displayInfoKey: "netTotalCost",
            title: "Net Cost",
            value: -netTotalCost,
            valueColor: getCardValueColor(-netTotalCost),
        },
        {
            displayInfoKey: "totalLikeForLikeCost",
            value: -(crp.splitPlanLifetimeTotalMeasureCosts - crp.splitPlanLifetimeIncrementalMeasureCosts),
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "incrementalCost",
            value: -crp.splitPlanLifetimeIncrementalMeasureCosts,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalIncentives",
            value: crp.planLifetimeIncentives,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalCostSavings",
            title: "Net Savings",
            value: netTotalSavings,
            valueColor: getCardValueColor(netTotalSavings),
        },
        {
            displayInfoKey: "totalCarbonPolicyCostSavings",
            value: crp.planLifetimeCarbonPolicySavings,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "totalEnergyCostSavings",
            value: crp.splitPlanLifetimeEnergyCostSavings,
            titleIcon: <>• </>,
        },
        {
            displayInfoKey: "revenue",
            value: crp.planLifetimeRevenue,
            titleIcon: <>• </>,
        },
    ];
    const data = {
        header: {
            displayInfoKey: "netPresentValueIncremental",
            value: planLifetimeNPVIncremental,
            valueColor:
                planLifetimeNPVIncremental > 0 ? "var(--audette-content-positive)" : "var(--audette-content-negative)",
        },
        sections: [firstSection],
    };

    return <Card loading={false} data={data} cardTitle="Cumulative Net Present Value" />;
}
